import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";

import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/es";

dayjs.extend(localizedFormat);
dayjs.extend(utc);

const SUPPORTED_LOCALES = new Set(["en", "fr", "es"]);

function resolveLocale(locale: string | null | undefined): string {
  if (!locale) return "en";

  const normalized = locale.toLowerCase().replace("_", "-");
  if (SUPPORTED_LOCALES.has(normalized)) return normalized;

  const baseLocale = normalized.split("-")[0];
  return SUPPORTED_LOCALES.has(baseLocale) ? baseLocale : "en";
}

export function getDisplayDate(
  startDate: string,
  startTime: string | null | undefined,
  dayStartTime: string | null | undefined,
  editionStartDate: string | null | undefined,
): string {
  if (!startTime || !dayStartTime) return startDate;
  const midnight = dayjs.utc("2020-01-01T00:00:00");
  const parsedDayStartTime = dayjs.utc("2020-01-01T" + dayStartTime);
  // Set a display start date for events that are between midnight and the edition's day start time
  const parsedStartTime = dayjs.utc("2020-01-01T" + startTime);
  const isEventDuringNight =
    parsedStartTime.isSame(midnight) ||
    (parsedStartTime.isAfter(midnight) &&
      parsedStartTime.isBefore(parsedDayStartTime));

  const isStartDate = startDate === editionStartDate;

  const parsedDate = dayjs
    .utc(startDate)
    .subtract(isEventDuringNight && !isStartDate ? 1 : 0, "day");

  return parsedDate.format("YYYY-MM-DD");
}

export function formatDate(
  date: string,
  format = "dddd, LL",
  locale?: string,
): string {
  return dayjs.utc(date).locale(resolveLocale(locale)).format(format);
}

export function getDaysBetween(startDate: string, endDate: string): string[] {
  const days: string[] = [];
  let current = dayjs.utc(startDate);
  const end = dayjs.utc(endDate);

  while (current.isBefore(end) || current.isSame(end, "day")) {
    days.push(current.format("YYYY-MM-DD"));
    current = current.add(1, "day");
  }

  return days;
}

export function formatTime(
  time: string | null | undefined,
  format: "24h" | "12h" = "24h",
): string {
  if (!time) return "";
  // Input: "06:00:00" -> Output: "06:00" (24h) or "6:00 AM" (12h)
  const hhmm = time.slice(0, 5);
  if (format === "24h") return hhmm;

  const [hours, minutes] = hhmm.split(":");
  const h = parseInt(hours, 10);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${minutes} ${period}`;
}

export { dayjs };
